import { useNotification } from "@/context/NotificationProvider";
import { cn } from "@/lib/utils";
import type { Notification } from "@/services/app/notification";
import { Bell, ChevronRight, Clock, RefreshCw, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const NotificationItem = ({
  item,
  onRead,
}: {
  item: Notification;
  onRead: (id: string) => void;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
    if (!item.read) {
      onRead(item.id);
    }
  }, [item, onRead]);

  const formattedDate = new Date(item.createdAt).toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <>
      <div
        className={cn(
          "border bg-white p-4 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md",
          !item.read
            ? "border-l-4 border-l-blue-500 border-b-gray-100 border-t-gray-100 border-r-gray-100"
            : "border-gray-100"
        )}
        onClick={handleOpenModal}
      >
        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900 leading-tight">
              {item.titulo}
            </h3>
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
              {item.descricao?.substring(0, 60)}
              {item.descricao?.length > 60 ? "..." : ""}
            </p>
            <div className="flex items-center mt-2">
              <Clock size={12} className="text-gray-400 mr-1" />
              <span className="text-xs text-gray-400">{formattedDate}</span>
            </div>
          </div>
          <ChevronRight size={16} className="text-gray-400" />
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {item.titulo}
                  </h2>
                  <div className="flex items-center">
                    <Clock size={14} className="text-gray-500 mr-1" />
                    <span className="text-xs font-medium text-gray-500">
                      {formattedDate}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-base leading-6 text-gray-800">
                  {item.descricao}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const NotificationPage = () => {
  const [activeFilter, setActiveFilter] = useState<"all" | "unread">("all");
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const { notifications, markAsRead, refetch, isLoading } = useNotification();

  const handleMarkAsRead = useCallback(
    async (id: string) => {
      await markAsRead(id);
    },
    [markAsRead]
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const filteredData = useMemo(() => {
    if (!notifications) return [];
    
    return activeFilter === "all"
      ? [...notifications]
      : notifications.filter((item) => !item.read);
  }, [notifications, activeFilter]);

  const unreadCount = useMemo(() => {
    if (!notifications) return 0;
    return notifications.filter((item) => !item.read).length;
  }, [notifications]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notificações</h1>
          <p className="text-gray-500 mt-1">Gerencie suas notificações</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X size={20} className="text-gray-600" />
        </button>
      </div>

      {/* Filtros */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveFilter("all")}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium transition-colors",
              activeFilter === "all"
                ? "bg-blue-100 text-blue-700"
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            Todas
          </button>
          <button
            onClick={() => setActiveFilter("unread")}
            className={cn(
              "flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors",
              activeFilter === "unread"
                ? "bg-blue-100 text-blue-700"
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            Não lidas
            {unreadCount > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-blue-500 text-white text-xs font-bold">
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {notifications && notifications.length > 0 && (
          <button
            onClick={onRefresh}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            disabled={refreshing}
          >
            <RefreshCw
              size={16}
              className={cn(
                "text-gray-600",
                refreshing && "animate-spin"
              )}
            />
          </button>
        )}
      </div>

      {/* Lista de notificações */}
      <div className="space-y-3">
        {notifications && filteredData.length > 0 ? (
          filteredData.map((item) => (
            <NotificationItem
              key={item.id}
              item={item}
              onRead={handleMarkAsRead}
            />
          ))
        ) : (
          <div className="text-center py-12">
            {isLoading ? (
              <div className="flex flex-col items-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 mb-4"></div>
                <p className="text-gray-500">Carregando notificações...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Bell size={28} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Nenhuma notificação
                </h3>
                <p className="text-sm text-gray-500 max-w-xs">
                  {activeFilter === "unread"
                    ? "Você não tem notificações não lidas no momento."
                    : "Você não tem notificações no momento."}
                </p>
                <button
                  onClick={onRefresh}
                  className="mt-6 px-6 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-800">
                    Atualizar
                  </span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPage; 